// 非同期Knowledge Base API用のReact Hook（Cognito認証統合版）
import { useState, useCallback, useRef, useEffect } from 'react';
import { 
  startAsyncKnowledgeBaseWithError,
  checkAsyncStatusWithError,
  startAsyncKnowledgeBase,
  checkAsyncStatus,
  waitForAsyncCompletion,
  executeAsyncKnowledgeBase
} from '../services/asyncKnowledgeBaseService';
import { 
  AsyncKnowledgeBaseStatusResponse,
  AsyncStatus,
  KnowledgeBaseResponse,
  Citation,
  ApiResult
} from '../types/ApiTypes';
import { formatCitations } from '../services/knowledgeBaseService';
import { appConfig } from '../config/env';

// Hook の戻り値の型定義（Cognito認証対応）
export interface UseAsyncKnowledgeBaseReturn {
  // 状態
  loading: boolean;
  error: string | null;
  progress: string;
  currentStatus: AsyncStatus | null;
  requestId: string | null;
  
  // 結果情報
  result: KnowledgeBaseResponse | null;
  citations: Array<{
    text: string;
    sources: Array<{
      content: string;
      metadata: Record<string, any>;
    }>;
  }>;
  
  // 関数（Cognito認証対応）
  startAsyncQuery: (query: string, idToken?: string, maxResults?: number, sessionId?: string) => Promise<string>;
  performQuery: (query: string, idToken?: string, maxResults?: number, sessionId?: string) => Promise<KnowledgeBaseResponse>;
  checkAsyncStatusWithError: (requestId: string, idToken?: string) => Promise<ApiResult<AsyncKnowledgeBaseStatusResponse>>;
  cancelQuery: () => void;
  clearResult: () => void;
  clearError: () => void;
}

// 非同期Knowledge Base API用のカスタムフック（Cognito認証対応）
export const useAsyncKnowledgeBase = (): UseAsyncKnowledgeBaseReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState('');
  const [currentStatus, setCurrentStatus] = useState<AsyncStatus | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [result, setResult] = useState<KnowledgeBaseResponse | null>(null);
  const [citations, setCitations] = useState<Array<{
    text: string;
    sources: Array<{
      content: string;
      metadata: Record<string, any>;
    }>;
  }>>([]);

  // キャンセル用のref
  const cancelRef = useRef<boolean>(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 非同期クエリ開始（Cognito認証対応）
  const startAsyncQuery = useCallback(async (
    query: string,
    idToken?: string,
    maxResults?: number,
    sessionId?: string
  ): Promise<string> => {
    setLoading(true);
    setError(null);
    setProgress('非同期処理を開始しています...');
    setCurrentStatus(AsyncStatus.QUEUED);
    cancelRef.current = false;

    try {
      const startResult = await startAsyncKnowledgeBaseWithError(
        query,
        idToken,
        maxResults,
        sessionId
      );

      if (startResult.success) {
        setRequestId(startResult.data.requestId);
        setProgress(startResult.data.message);
        setCurrentStatus(startResult.data.status);
        return startResult.data.requestId;
      } else {
        const errorMessage = startResult.error.message || startResult.error.error || 'Unknown error occurred';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      setLoading(false);
      throw error;
    }
  }, []);

  // ステータスポーリング（Cognito認証対応）
  const pollStatus = useCallback(async (
    requestId: string,
    idToken?: string
  ): Promise<KnowledgeBaseResponse | null> => {
    const pollInterval = 2000; // 2秒間隔
    const maxPollTime = 300000; // 5分
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        if (cancelRef.current) {
          resolve(null);
          return;
        }

        if (Date.now() - startTime > maxPollTime) {
          reject(new Error('非同期処理がタイムアウトしました'));
          return;
        }

        try {
          const statusResult = await checkAsyncStatusWithError(requestId, idToken);

          if (statusResult.success) {
            const status = statusResult.data;
            setCurrentStatus(status.status);

            if (status.status === AsyncStatus.COMPLETED) {
              const completedResponse = status as any;
              // バックエンドのレスポンス構造に合わせて修正
              if (completedResponse.response) {
                setProgress('処理が完了しました');
                resolve(completedResponse.response);
                return;
              } else {
                reject(new Error('完了レスポンスにresponseデータがありません'));
                return;
              }
            } else if (status.status === AsyncStatus.FAILED) {
              const failedResponse = status as any;
              const errorMessage = failedResponse.error || failedResponse.message || '非同期処理が失敗しました';
              setError(errorMessage);
              reject(new Error(errorMessage));
              return;
            } else if (status.status === AsyncStatus.DB_RESUMING) {
              // Aurora Serverless復旧中の処理
              const resumingResponse = status as any;
              const resumingMessage = resumingResponse.message || 'データベースが起動中です。しばらくお待ちください...';
              setProgress(resumingMessage);
              
              // retryAfterが指定されている場合は、その時間だけ長く待機
              const retryAfter = resumingResponse.retryAfter || 30; // デフォルト30秒
              const extendedPollInterval = Math.max(pollInterval, retryAfter * 1000);
              
              pollIntervalRef.current = setTimeout(poll, extendedPollInterval);
            } else {
              // まだ処理中
              setProgress(status.message || '処理中...');
              pollIntervalRef.current = setTimeout(poll, pollInterval);
            }
          } else {
            const errorMessage = statusResult.error.message || 'ステータス確認に失敗しました';
            setError(errorMessage);
            reject(new Error(errorMessage));
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'ステータス確認エラー';
          setError(errorMessage);
          reject(error);
        }
      };

      poll();
    });
  }, []);

  // 完全な非同期クエリ実行（開始から完了まで、Cognito認証対応）
  const performQuery = useCallback(async (
    query: string,
    idToken?: string,
    maxResults?: number,
    sessionId?: string
  ): Promise<KnowledgeBaseResponse> => {
    try {
      // 非同期処理開始
      const requestId = await startAsyncQuery(query, idToken, maxResults, sessionId);
      
      // 完了まで待機
      const result = await pollStatus(requestId, idToken);
      
      if (result) {
        setResult(result);
        
        // 引用情報を整理
        if (result.citations) {
          const formattedCitations = formatCitations(result.citations);
          setCitations(formattedCitations);
        }
        
        setLoading(false);
        return result;
      } else {
        throw new Error('非同期処理がキャンセルされました');
      }
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, [startAsyncQuery, pollStatus]);

  // クエリキャンセル
  const cancelQuery = useCallback(() => {
    cancelRef.current = true;
    if (pollIntervalRef.current) {
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setLoading(false);
    setProgress('処理がキャンセルされました');
    setCurrentStatus(null);
  }, []);

  // 結果クリア
  const clearResult = useCallback(() => {
    setResult(null);
    setCitations([]);
    setProgress('');
    setCurrentStatus(null);
    setRequestId(null);
    setError(null);
  }, []);

  // エラークリア
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ステータス確認（エラーハンドリング付き）
  const checkAsyncStatusWithError = useCallback(async (
    requestId: string,
    idToken?: string
  ): Promise<ApiResult<AsyncKnowledgeBaseStatusResponse>> => {
    if (appConfig.debugMode) {
      console.log(`[DEBUG] Checking async status with error handling: ${requestId}`);
    }

    try {
      const response = await checkAsyncStatus(requestId, idToken);
      
      return {
        success: true,
        data: response
      };
    } catch (error) {
      if (appConfig.debugMode) {
        console.error(`[DEBUG] Async Status Check Error:`, error);
      }
      
      return {
        success: false,
        error: {
          error: error instanceof Error ? error.name : 'Unknown Error',
          message: error instanceof Error ? error.message : 'An unknown error occurred'
        }
      };
    }
  }, []);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
      }
    };
  }, []);

  return {
    // 状態
    loading,
    error,
    progress,
    currentStatus,
    requestId,
    
    // 結果情報
    result,
    citations,
    
    // 関数
    startAsyncQuery,
    performQuery,
    checkAsyncStatusWithError,
    cancelQuery,
    clearResult,
    clearError,
  };
};
